import { useStore } from '@/store';
import type { StoreSlice } from '@/store';
import { clearPersistedSave } from '@/utils';
import { safeAsync, stringifyState } from '@morph-mapper/utils';
import { usePlugins } from '@morph-mapper/plugins';
import { useUpdateTemplate } from '../api/templates/query';
import type { TreeNode } from '@morph-mapper/types';

// Runtime evaluation results, re-derived from the active file on load.
const stripComputed = (entries: Record<string, TreeNode>) =>
  Object.fromEntries(
    Object.entries(entries).map(([id, { computed, ...entry }]) => [id, entry]),
  );

export const buildTemplateSave = (
  { config, entries, graphs, ai }: StoreSlice,
  id: string,
) => ({
  id,
  operatorId: config.getOperatorId(),
  name: config.getName(),
  domain: config.getDomain(),
  type: config.getType(),
  variant: config.getVariant(),
  schemaId: config.getSchemaId(),
  graphs: graphs.graphs,
  entries: stripComputed(entries.entries),
  suggestions: ai.suggestions,
  config: {
    updateDiscardIds: config.getDiscardFields(),
    preconfiguration: config.getPreconfiguration(),
    configSnapshot: config.getConfigSnapshot(),
    lastTextContentHash: config.getLastTextContentHash(),
  },
});

let saveInFlight: Promise<boolean> | null = null;

export const useSaveState = () => {
  const setIsSavingTemplate = useStore(
    ({ config }) => config.setIsSavingTemplate,
  );
  const { mutateAsync: updateTemplate } = useUpdateTemplate();
  const { serialize } = usePlugins();

  const performSave = async (): Promise<boolean> => {
    const state = useStore.getState();

    const id = state.config.id;
    if (!id) {
      setIsSavingTemplate(false);
      return false;
    }

    const result = await safeAsync(() =>
      updateTemplate({
        id,
        template: {
          save: stringifyState({
            ...buildTemplateSave(state, id),
            plugins: serialize(),
          }),
        },
      }),
    );

    setIsSavingTemplate(false);
    if (result.isErr()) {
      return false;
    }

    clearPersistedSave();
    return true;
  };

  const save = async (): Promise<boolean> => {
    if (!saveInFlight) {
      saveInFlight = performSave();
    }

    try {
      return await saveInFlight;
    } finally {
      saveInFlight = null;
    }
  };

  return { save };
};
